/**
 * Shared "thing on a track" math used by moving platforms and moving anchors.
 *
 * A `PathSpec` is a polyline of waypoints traversed at a constant px/sec speed.
 * Two loop modes:
 *   - `cycle`     — A→B→C→A→B→C…  (teleports back to start; good for orbits)
 *   - `pingpong`  — A→B→C→B→A→…   (reverses direction at each end)
 *
 * The schema stores ABSOLUTE world coordinates for each waypoint. The
 * `pathPosition` helper returns an interpolated point at any time `t` (in
 * seconds since level start). An optional `phase` (also in seconds) lets
 * authors stagger multiple movers that share a path shape.
 *
 * Determinism: time is the only input — no Math.random, no DOM clock — so
 * replays reproduce the exact same motion for a given level frame count.
 */

export type PathLoop = 'cycle' | 'pingpong';

export interface PathSpec {
  points: Array<{ x: number; y: number }>;
  /** Travel speed along the polyline, in pixels per second. Must be > 0. */
  speed: number;
  loop: PathLoop;
  /** Time offset in seconds. Defaults to 0. */
  phase?: number;
}

/** Total length of the polyline in pixels. Returns 0 for degenerate paths. */
function polylineLength(points: ReadonlyArray<{ x: number; y: number }>): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i]!.x - points[i - 1]!.x;
    const dy = points[i]!.y - points[i - 1]!.y;
    total += Math.hypot(dx, dy);
  }
  return total;
}

/**
 * Interpolate the path at `tSec`. For 1-point paths the result is that point.
 * For 0-point paths the result is `{x:0, y:0}`.
 */
export function pathPosition(spec: PathSpec, tSec: number): { x: number; y: number } {
  const pts = spec.points;
  if (pts.length === 0) return { x: 0, y: 0 };
  if (pts.length === 1) return { x: pts[0]!.x, y: pts[0]!.y };

  const segLen: number[] = new Array(pts.length - 1);
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i]!.x - pts[i - 1]!.x;
    const dy = pts[i]!.y - pts[i - 1]!.y;
    const L = Math.hypot(dx, dy);
    segLen[i - 1] = L;
    total += L;
  }
  if (total <= 0) return { x: pts[0]!.x, y: pts[0]!.y };

  const phase = spec.phase ?? 0;
  // Distance traveled (px) since t=0, modulo the period.
  const traveled = (tSec + phase) * spec.speed;
  let along: number;
  if (spec.loop === 'pingpong') {
    // Period covers a full out-and-back trip.
    const period = total * 2;
    let m = traveled % period;
    if (m < 0) m += period;
    along = m <= total ? m : period - m;
  } else {
    let m = traveled % total;
    if (m < 0) m += total;
    along = m;
  }

  // Find which segment `along` falls into.
  let acc = 0;
  for (let i = 0; i < segLen.length; i++) {
    const L = segLen[i]!;
    if (along <= acc + L || i === segLen.length - 1) {
      const local = L > 0 ? (along - acc) / L : 0;
      const a = pts[i]!;
      const b = pts[i + 1]!;
      return { x: a.x + (b.x - a.x) * local, y: a.y + (b.y - a.y) * local };
    }
    acc += L;
  }
  return { x: pts[pts.length - 1]!.x, y: pts[pts.length - 1]!.y };
}

/** True if the spec has at least 2 distinct points and a positive speed. */
export function isMovingPath(spec: PathSpec | undefined): spec is PathSpec {
  if (!spec) return false;
  if (spec.points.length < 2) return false;
  if (!(spec.speed > 0)) return false;
  return polylineLength(spec.points) > 0.001;
}
